import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHero } from "@/components/Sections";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, AlertTriangle } from "lucide-react";

export default function SpaceToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/today");
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const apod = data?.apod;
  const fact = data?.random_fact;

  return (
    <>
      <PageHero
        eyebrow="Space Today"
        title="What's happening"
        accent="in space, right now."
        subtitle="Your daily summary — today's astronomy image, the latest from Mars and Earth orbit, and the asteroids passing by today."
      />

      <section className="max-w-7xl mx-auto px-6 pb-12">
        {loading ? (
          <Skeleton className="h-[55vh] w-full rounded-3xl" />
        ) : apod ? (
          <div className="glass-strong p-5 md:p-8 fade-in-up">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                {apod.media_type === "video" ? (
                  <div className="aspect-video rounded-2xl overflow-hidden">
                    <iframe src={apod.url} title={apod.title} className="w-full h-full" frameBorder="0" allowFullScreen />
                  </div>
                ) : (
                  <img src={apod.hdurl || apod.url} alt={apod.title} className="w-full rounded-2xl" loading="lazy" />
                )}
              </div>
              <div className="lg:col-span-5">
                <div className="eyebrow">Astronomy Picture of the Day · {apod.date}</div>
                <h2 className="display mt-4 text-3xl md:text-4xl">{apod.title}</h2>
                <p className="mt-4 text-white/70 leading-relaxed text-[15px] line-clamp-6">{apod.explanation}</p>
                <Link to="/apod" className="btn-pill btn-secondary mt-6">Read more <ArrowRight size={14} /></Link>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-2 gap-5 stagger">
          {data?.mars_photo && (
            <Link to="/mars-rover" className="glass hover-lift overflow-hidden block" data-testid="today-mars-card">
              <div className="aspect-[16/10] overflow-hidden bg-white/5">
                <img src={data.mars_photo.img_src} alt="Mars latest" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <div className="eyebrow">Mars · {data.mars_photo.rover}</div>
                <div className="display mt-3 text-2xl">Latest from {data.mars_photo.rover}</div>
                <div className="mt-2 text-sm text-white/55">{data.mars_photo.camera} · {data.mars_photo.earth_date}</div>
              </div>
            </Link>
          )}
          {data?.earth_photo && (
            <Link to="/earth" className="glass hover-lift overflow-hidden block" data-testid="today-earth-card">
              <div className="aspect-[16/10] overflow-hidden bg-white/5">
                <img src={data.earth_photo.url} alt="Earth" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <div className="eyebrow">Earth · Deep-space imaging</div>
                <div className="display mt-3 text-2xl">Earth, captured today</div>
                <div className="mt-2 text-sm text-white/55">{data.earth_photo.date}</div>
              </div>
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-3 gap-5 stagger">
          <div className="glass p-7">
            <div className="eyebrow">Asteroids today</div>
            <div className="display mt-4 text-5xl">{data?.neo_count ?? "—"}</div>
            <div className="mt-3 text-sm text-white/55">Tracked near-Earth objects</div>
            <Link to="/asteroids" className="mt-5 inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white">
              View tracker <ArrowRight size={14} />
            </Link>
          </div>
          <div className="glass p-7">
            <div className="eyebrow">Potentially hazardous</div>
            <div className="display mt-4 text-5xl text-red-300 flex items-center gap-3">
              {data?.neo_hazardous_count ?? "—"}
              {(data?.neo_hazardous_count || 0) > 0 && <AlertTriangle size={28} />}
            </div>
            <div className="mt-3 text-sm text-white/55">By our hazard classification</div>
          </div>
          <div className="glass p-7">
            <div className="eyebrow">Closest approach</div>
            <div className="display mt-4 text-3xl">
              {data?.neo_top?.[0]?.name || "—"}
            </div>
            <div className="mt-3 text-sm text-white/55">
              {data?.neo_top?.[0]?.miss_distance_km ? `${Number(data.neo_top[0].miss_distance_km).toLocaleString(undefined, { maximumFractionDigits: 0 })} km` : ""}
            </div>
          </div>
        </div>
      </section>

      {fact && (
        <section className="max-w-7xl mx-auto px-6 pb-24">
          <div className="glass-strong p-8 md:p-12 fade-in-up">
            <div className="eyebrow-row"><span className="eyebrow">Random space fact</span></div>
            <div className="grid md:grid-cols-12 gap-8 mt-6 items-center">
              {fact.media_type === "image" && (
                <div className="md:col-span-5">
                  <img src={fact.url} alt={fact.title} className="w-full rounded-2xl" loading="lazy" />
                </div>
              )}
              <div className={fact.media_type === "image" ? "md:col-span-7" : "md:col-span-12"}>
                <div className="eyebrow">{fact.date}</div>
                <h3 className="display mt-3 text-3xl md:text-4xl">{fact.title}</h3>
                <p className="mt-4 text-white/70 leading-relaxed text-[15px] line-clamp-6">{fact.explanation}</p>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
